<template>
  <div class="flex-shrink flex-grow overflow-auto p-4">
    <div class="flex flex-col">
      <div class="flex flex-col h-full">
        <div class="bg-white flex-1 h-full">
          <ve-table
            :fixed-header="true"
            :columns="columns"
            :table-data="tableData"
            :border-around="false"
            :sort-option="sortOption"
            :checkbox-option="checkboxOption"
            row-key-field-name="id"
          />
          <empty-state
            v-if="showEmptyState"
            :title="$t('FILE_STORAGE.LIST.NO_RECORDS')"
          />
          <div
            v-if="loading"
            class="items-center flex text-base justify-center"
          >
            <spinner />
            <span>{{ $t('FILE_STORAGE.LIST.LOADING_MESSAGE') }}</span>
          </div>
        </div>
        <table-footer
          :current-page="Number(meta.current_page)"
          :total-count="meta.all_count"
          :page-size="25"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Spinner from 'shared/components/Spinner.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import timeMixin from 'dashboard/mixins/time';

export default {
  name: 'FileStorageIndex',
  components: {
    Spinner,
    EmptyState,
    VeTable,
    TableFooter,
  },
  mixins: [rtlMixin, timeMixin],
  data() {
    return {
      sortOption: {
        sortAlways: true,
        // eslint-disable-next-line no-return-assign
        sortChange: params => {
          this.sortConfig = params;
          this.$store.dispatch('fileStorage/fetchRecords');
        },
      },
      checkboxOption: {
        selectedRowKeys: [],
        selectedRowChange: ({ selectedRowKeys }) => {
          this.checkboxOption.selectedRowKeys = selectedRowKeys;
          this.$store.commit('fileStorage/SET_SELECTED_ROWS', selectedRowKeys);
        },
        selectedAllChange: ({ selectedRowKeys }) => {
          this.checkboxOption.selectedRowKeys = selectedRowKeys;
          this.$store.commit('fileStorage/SET_SELECTED_ROWS', selectedRowKeys);
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      attachments: 'fileStorage/getAttachments',
      meta: 'fileStorage/getMeta',
      loading: 'fileStorage/getLoading',
      getSortConfig: 'fileStorage/getSortConfig',
      currentAccountId: 'getCurrentAccountId',
    }),
    sortConfig: {
      get() {
        return this.getSortConfig;
      },
      set(value) {
        this.$store.commit('fileStorage/SET_SORT_CONFIG', value);
      },
    },
    showEmptyState() {
      return !this.loading && this.attachments?.length === 0;
    },
    tableData() {
      if (this.loading) {
        return [];
      }
      return (this.attachments || []).map(
        ({
          location,
          location_metadata,
          created_at,
          filename,
          byte_size,
          url,
          id,
        }) => {
          return {
            location,
            id,
            url,
            created_at: this.messageStamp(created_at, 'dd.MM.yyyy h:mm:ss'),
            filename,
            conversationUrl: this.makeConversationUrl(location_metadata || {}),
            byte_size: `${(byte_size / 1024 / 1024).toFixed(2)}`,
          };
        }
      );
    },
    columns() {
      return [
        {
          field: '',
          key: 'a',
          type: 'checkbox',
          title: '',
          width: 50,
          align: 'center',
        },
        {
          field: 'filename',
          key: 'filename',
          sortBy: this.sortConfig.filename || '',
          title: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.FILENAME'),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => (
            <a href={row.url} target="_blank">
              {row.filename}
            </a>
          ),
        },
        {
          field: 'byte_size',
          key: 'byte_size',
          sortBy: this.sortConfig.byte_size || '',
          title: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.SIZE'),
          align: this.isRTLView ? 'right' : 'left',
        },
        {
          field: 'location',
          key: 'location',
          title: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.LOCATION'),
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => {
            return row.conversationUrl ? (
              <a href={row.conversationUrl} target="_blank">
                {row.location}
              </a>
            ) : (
              row.location
            );
          },
        },
        {
          field: 'created_at',
          key: 'created_at',
          sortBy: this.sortConfig.created_at || '',
          title: this.$t('FILE_STORAGE.LIST.TABLE_HEADER.ADDED_DATE'),
          align: this.isRTLView ? 'right' : 'left',
        },
      ];
    },
  },
  watch: {
    $route() {
      this.$store.dispatch('fileStorage/fetchRecords');
    },
  },
  async mounted() {
    this.$store.dispatch('fileStorage/fetchRecords');
  },
  methods: {
    onPageChange(page) {
      this.$store.dispatch('fileStorage/fetchRecords', { page });
    },
    makeConversationUrl({ id, type }) {
      switch (type) {
        case 'Conversation':
          return `/app/accounts/${this.currentAccountId}/conversations/${id}`;
        case 'Contact':
          return `/app/accounts/${this.currentAccountId}/contacts/${id}`;
        default:
          return null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep td {
  overflow: hidden;
  text-overflow: ellipsis;
}
.ve-table {
  @apply bg-white dark:bg-slate-900;

  &::v-deep {
    .ve-table-container {
      overflow-y: hidden;
    }
  }
}
</style>
